import * as _ from "lodash";
import { v1 as uuid } from "uuid";
import firebase from "firebase/compat/app";
import { of } from "rxjs";
import { Navigate, RouterState } from "@ngxs/router-plugin";

import { ApiService } from "../services/api.service";
import { FcmService } from "../services/fcm.service";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { finalize, tap } from "rxjs/operators";

import {
  State,
  Action,
  StateContext,
  Selector,
  Store,
  createSelector,
} from "@ngxs/store";
import { User } from "../interface/user";
import { Injectable } from "@angular/core";
import { patch, updateItem } from "@ngxs/store/operators";
import { AlertService } from "../services/alert.service";
import {
  AdminSubscribeToPlace,
  AllCountries,
  CleanSelectedPlace,
  DeleteDeliveryPlace,
  FirebaseAuth,
  GetCoins,
  GetPermission,
  GetPermissionsSubmodules,
  GetPlaces,
  GetSubmodules,
  GoogleLogin,
  Login,
  LoginGhost,
  Ninja,
  PlaceProfile,
  ResetPasswordEmail,
  SaveImageRouteForProfile,
  SendRequest,
  SignOut,
  SubscribeToPlace,
  SusbcribeToFCM,
  SuscribeToNotification,
  UpdateDefaultCoin,
  UpdatedUserState,
  UpdateNotAvailableProducts,
  UpdatePlace,
  UpdatePlaceDeliveriesCoordinates,
  UpdatePlaceLogo,
  UpdateProfileImage,
  UpdateSelectedPlace,
  UpdateUserSession,
  UploadProfileImage,
  UserByUID,
  UsernameByToken,
  WatchIdToken,
} from "./actions/user.actions";
import { AngularFireStorage } from "@angular/fire/compat/storage";
import { environment } from "src/environments/environment";
import { CookieService } from "ngx-cookie-service";
import { getInitMenu } from "../helpers/helpers";
import { DataService } from "../services/data.service";

@State<User>({
  name: "user",
  defaults: {
    isGhost: "1",
    selectedCart: null,
    selectedCartOfInfo: null,
    carts: [],
    access: [],
    menu: getInitMenu(),
  },
})
@Injectable()
export class UserState {
  @Selector()
  static user(state: User) {
    return state;
  }

  @Selector()
  static me({ me }: User) {
    return me;
  }

  @Selector()
  static phoneInvalid({ phoneInvalid }: User) {
    return phoneInvalid;
  }

  @Selector()
  static menu({ menu }: User) {
    return menu;
  }

  @Selector()
  static devideAdded({ devideAdded }: User) {
    return devideAdded;
  }

  @Selector()
  static defaultCoin(state: User) {
    return state.me.DefaultCoin;
  }

  @Selector()
  static username(state: User) {
    return state.username;
  }

  @Selector()
  static followedPlaces(state: User) {
    return state.me.FollowedPlaces;
  }

  @Selector()
  static token({ token }: User) {
    return token;
  }

  @Selector()
  static places({ places }: User) {
    return places;
  }

  @Selector()
  static submodules({ submodules }: User) {
    return submodules;
  }

  @Selector()
  static selectedPlace({ selectedPlace }: User) {
    return selectedPlace;
  }

  @Selector()
  static admin({ admin }: User) {
    return admin;
  }

  @Selector()
  static orders({ orders }: User) {
    return orders;
  }

  @Selector()
  static carts({ carts }: User) {
    return carts;
  }

  @Selector()
  static notSelectedCarts({ carts, selectedCart }: User) {
    return selectedCart
      ? carts.filter((_cart) => _cart.id !== selectedCart.id)
      : carts;
  }

  @Selector()
  static UserSelected(state: User) {
    return state.UserSelected;
  }

  @Selector()
  static sudo(state: User) {
    return state.me.Sudo;
  }

  @Selector()
  static DeliveryRole(state: User) {
    return state.me.DeliveryRole;
  }

  @Selector()
  static isDelivery(state: User) {
    return state.me.DeliveryRole.Name_Role === "Delivery";
  }

  @Selector()
  static FilteredBills(state: User) {
    return state.FilteredBills;
  }

  @Selector()
  static selectedCart(state: User) {
    return state.selectedCart;
  }

  @Selector()
  static selectedCartLength(state: User) {
    if (state.selectedCart.Detail.length > 0) {
      return state.selectedCart.Detail.length;
    }
    return null;
  }

  @Selector()
  static selectedCartOfInfo(state: User) {
    return state.selectedCartOfInfo;
  }

  @Selector()
  static notAvailableBillDetails(state: User) {
    return state.notAvailableBillDetails;
  }

  @Selector()
  static coins(state: User) {
    return state.coins;
  }

  @Selector()
  static colon({ coins }: User) {
    return coins.find((coin) => coin.id === "1");
  }

  @Selector()
  static isGhost(state: User) {
    return state.isGhost;
  }

  @Selector()
  static billProgress({ billProgress }: User) {
    return billProgress;
  }

  @Selector()
  static firebaseUser({ firebaseUser }: User) {
    return firebaseUser;
  }
  @Selector()
  static firebaseUserName({ firebaseUser }: User) {
    return firebaseUser.Given_Name;
  }

  @Selector()
  static UID({ firebaseUser: { UID } }: User) {
    return UID;
  }

  @Selector()
  static countries({ countries }: User) {
    return countries;
  }

  @Selector()
  static addresses({
    me: {
      Person: { Addresses },
    },
  }: User) {
    return Addresses;
  }

  @Selector()
  static accounts({
    me: {
      Person: { Accounts },
    },
  }: User) {
    return Accounts;
  }

  @Selector()
  static loadMoreOrders(state: User) {
    return state.loadMoreOrders;
  }

  @Selector([RouterState.state])
  static dataRoute({ submodules }: User, { url }) {
    const submodule = submodules.find((sub) => sub.Route_Submodule === url);
    if (submodule || submodule.Permissions.length > 0) {
      const send = submodule.Permissions.reduce((_permissions, current) => {
        _permissions[current.Name_Permission] = true;
        return _permissions;
      }, {});
      return send;
    } else {
      return {};
    }
  }

  @Selector()
  static isAuthenticated(state: User) {
    return !!state.token && !!state.firebaseUser;
  }
  static route(url: string) {
    return createSelector([UserState], (state: User) => {
      state.submodules.find((sub) => sub.Route_Submodule === url);
      const submodule = state.submodules.find(
        (sub) => sub.Route_Submodule === url
      );
      if (submodule || submodule.Permissions.length > 0) {
        const send = submodule.Permissions.reduce((_permissions, current) => {
          _permissions[current.Name_Permission] = true;
          return _permissions;
        }, {});
        return send;
      }
    });
  }

  constructor(
    private api: ApiService,
    private fcm: FcmService,
    private store: Store,
    public afAuth: AngularFireAuth,
    private alertService: AlertService,
    private storage: AngularFireStorage,
    private cookieService: CookieService,
    private _dataService: DataService
  ) {}

  ngxsOnInit({ dispatch }: StateContext<User>) {
    dispatch(new WatchIdToken());
    this.fcm.receiveMessages();
  }

  @Action(UpdateUserSession)
  UpdateUserSession(
    { patchState, dispatch, getState }: StateContext<User>,
    { info }: UpdateUserSession
  ) {
    const { firebaseUser, me, token, isGhost } = info;
    dispatch(new GetPlaces());
    patchState({ firebaseUser, me, token, isGhost });
  }

  @Action(WatchIdToken)
  watchIdToken({ dispatch, getState }: StateContext<User>) {
    this.afAuth.useDeviceLanguage();
    dispatch(new GetCoins());

    this.afAuth.onIdTokenChanged((user) => {
      if (user) {
        user
          .getIdTokenResult(true)
          .then((idToken) => {
            const {
              claims: { user_id },
              token,
              signInProvider,
            } = idToken;

            if (signInProvider !== "anonymous") {
              dispatch(new UserByUID(token, user_id));
              const state = getState();
              if (state && state.selectedPlace) {
                dispatch(new GetSubmodules(state.selectedPlace));
              }
            } else {
              const info = {
                token,
                isGhost: "1",
                firebaseUser: { UID: user_id },
                me: null,
              };
              dispatch(new UpdateUserSession(info));
            }
          })
          .catch((error) => {
            console.error("Error al obtener el token:", error);
          });
      }
    });
  }

  @Action(UserByUID)
  userByUID(
    { dispatch, getState, patchState }: StateContext<User>,
    { uid, navigate }: UserByUID
  ) {
    return this.api.userByUID(uid).pipe(
      tap(
        ({ data: { firebaseUser } }) => {
          if (firebaseUser) {
            if (firebaseUser.TavuelUser) {
              const info = {
                firebaseUser,
                me: firebaseUser.TavuelUser,
                token: "",
                isGhost: "0",
              };
              dispatch(new UpdateUserSession(info));
            } else {
              dispatch(new SignOut());
            }
          } else {
            const info = {
              firebaseUser: { UID: uid },
              me: null,
              token: "",
              isGhost: "1",
            };
            dispatch(new UpdateUserSession(info));
            dispatch(new Navigate(["/auth"]));
          }
        },
        async (graphQLErrors) => {
          if (graphQLErrors && graphQLErrors.length > 0) {
            this.alertService.openSnackBar(graphQLErrors[0].message, "error");
          }
          if (graphQLErrors.networkError.status === 401) {
            const user = await this.afAuth.currentUser;
            const newToken = await user.getIdToken(true);
            patchState({ token: newToken });
          }
        }
      )
      //trace("UserByUID")
    );
  }

  @Action(SignOut)
  signOut({ setState, dispatch }: StateContext<User>) {
    return this.afAuth.signOut().then(() => {
      setState(null);
      this.cookieService.set("user_token", "");
      localStorage.clear();
      dispatch(new Navigate(["/auth"]));
    });
  }

  @Action(GoogleLogin)
  googleLogin({ dispatch }: StateContext<User>, { loader }: GoogleLogin) {
    this.afAuth
      .signInWithPopup(new firebase.auth.GoogleAuthProvider())
      .then((credentials: any) => {
        setTimeout(() => {
          dispatch(new FirebaseAuth(credentials, credentials.user.uid));
        }, 1000);
      })
      .catch(() => {
        loader.close();
        this.alertService.openSnackBar("Error de conexión.", "error");
      });
  }

  @Action(FirebaseAuth)
  firebaseAuth(
    { getState, dispatch }: StateContext<User>,
    { user, ghostUID }: FirebaseAuth
  ) {
    const {
      additionalUserInfo: { providerId, profile },
      user: { uid, email, emailVerified, phoneNumber, displayName, photoURL },
    } = user;

    let signInUser: any = {
      Email: email,
      Picture: photoURL,
      Name: displayName,
      Verified_Email: emailVerified,
      Phone_Number: phoneNumber,
      UID: uid,
      Provider_Id: providerId,
      GhostUID: ghostUID,
    };
    if (profile) {
      const { family_name, given_name, locale } = profile;
      signInUser = {
        ...signInUser,
        Locale: locale || navigator.language,
        Given_Name: given_name,
        Family_Name: family_name,
      };
    } else {
      signInUser = {
        ...signInUser,
        Locale: navigator.language,
      };
    }
    return this.api.firebaseAuth(signInUser).pipe(
      tap(async ({ data: { firebaseUser } }) => {
        if (firebaseUser) {
          const token = await user.user.getIdToken();
          dispatch(new UserByUID(uid, true));
        } else {
          dispatch(new Navigate(["/auth"]));
        }
      })
      //trace("FirebaseAuth")
    );
  }

  @Action(AllCountries)
  allCountries({ getState, patchState }: StateContext<User>) {
    return this.api.getAllCountries().pipe(
      tap(({ data: { countries } }) => {
        patchState({ countries });
      })
      //trace("AllCountries")
    );
  }

  @Action(Login)
  login({ dispatch }: StateContext<User>, { credentials }: Login) {
    const ghostUID = this.store.selectSnapshot(UserState.UID);
    return this.afAuth
      .signInWithEmailAndPassword(credentials.email, credentials.password)
      .then((userCredentials) => {
        dispatch(new FirebaseAuth(userCredentials, ghostUID));
      })
      .catch(({ message }) => {
        this.alertService.openSnackBar(
          "Ha ocurrido un error al iniciar sesión",
          "error"
        );
      });
  }

  @Action(UsernameByToken)
  usernameByToken(
    { patchState, getState, dispatch }: StateContext<User>,
    { meToken }: UsernameByToken
  ) {
    return this.api.usernameByToken(meToken).pipe(
      tap(
        ({ data: { username } }) => {
          patchState({ username });
        },
        () => {
          this.alertService.openSnackBar(
            "Usuario o contraseña incorrecta",
            "error"
          );
        }
      )
      //trace("UsernameByToken")
    );
  }

  @Action(UpdatedUserState)
  UpdatedUserState(
    { patchState, getState }: StateContext<User>,
    { id, FK_UserState }: UpdatedUserState
  ) {
    const { me } = getState();
    return this.api.updatedUserState({ id, FK_UserState }).pipe(
      tap(({ data: { user } }) => {
        const newMe = { ...me, UserState: { id: 1, Name_UserState: "Active" } };
        patchState({ me: newMe });
      })
    );
  }

  @Action(SendRequest)
  sendRequest({ getState }: StateContext<User>, { message }: SendRequest) {
    return this.api.sendRequest(message);
  }

  @Action(LoginGhost)
  loginGhost({ getState, dispatch }: StateContext<User>) {
    const state = getState();
    if (!state || !state.me) {
      this.afAuth
        .signInAnonymously()
        .then((res) => {
          dispatch(new Navigate(["/auth"]));
        })
        .catch((n) => {
          this.alertService.openSnackBar("Error al loguear al ghost.", "error");
        });
    }
  }

  @Action(ResetPasswordEmail)
  ResetPasswordEmail({}: StateContext<User>, { email }: ResetPasswordEmail) {
    return this.afAuth
      .sendPasswordResetEmail(email)
      .then(() => {
        this.alertService.openSnackBar("Email enviado con éxito.", "success");
      })
      .catch((error) => {
        const errorMessage = this.translateFirebaseError(error.code);
        this.alertService.openSnackBar(errorMessage, "error");
      });
  }

  private translateFirebaseError(errorCode: string): string {
    const errorMessages: { [key: string]: string } = {
      "auth/invalid-email": "El correo electrónico no es válido.",
      "auth/user-not-found": "No existe una cuenta con este correo.",
      "auth/network-request-failed": "Error de conexión. Verifica tu internet.",
      "auth/too-many-requests": "Demasiados intentos. Inténtalo más tarde.",
      "auth/internal-error": "Ocurrió un error interno. Inténtalo de nuevo.",
      "auth/missing-email": "Debes ingresar un correo electrónico.",
    };

    return errorMessages[errorCode] || "Error desconocido al enviar el correo.";
  }

  @Action(GetPlaces)
  placesByUser({ patchState, getState, dispatch }: StateContext<User>) {
    const {
      me: { id, NotificationDevices },
    } = getState();
    return this.api.placesByUser(id).pipe(
      tap(
        ({ data: { places } }) => {
          if (places && places.length > 0) {
            const newPlaces = [];
            places.map((x) => 
              newPlaces.push({...x, Show_Notifications : NotificationDevices.find((nd) => nd.State_NotificationDevice && nd.FK_Place === x.id) })
            );
            newPlaces.sort((x, y) => x.Name_Place.localeCompare(y.Name_Place));
            patchState({ places: newPlaces });
          } else {
            this.alertService.openSnackBar(
              "No tienes acceso a esta parte de la aplicación.",
              "error"
            );
            dispatch(new SignOut());
          }
        },
        () => {
          this.alertService.openSnackBar(
            "Error al obtener lugares por usuario.",
            "error"
          );
        }
      )
      //trace("GetPlaces")
    );
  }

  @Action(GetSubmodules)
  getSubmodules(
    { patchState, getState, dispatch }: StateContext<User>,
    { place }: GetSubmodules
  ) {
    return this.api
      .submodulesByPlaceAndModuleAndRole(place.id, place.Role.id)
      .pipe(
        tap(
          ({ data: { submodules } }) => {
            let admin = false;
            let orders = false;
            submodules.forEach((_submodule) => {
              if (
                _submodule.Route_Submodule === "/admin" &&
                _submodule.Permissions.length > 0
              ) {
                admin = true;
              } else if (
                _submodule.Route_Submodule === "/orders" &&
                _submodule.Permissions.length > 0
              ) {
                orders = true;
              }
            });
            const menu = getInitMenu();
            const newMenu = menu.map((item) => {
              const module = submodules.find(
                (submodule) => submodule.id === item.id
              );
              return {
                ...item,
                isShow: !!(module?.Permissions?.length > 0),
              };
            });
            patchState({
              submodules,
              selectedPlace: place,
              admin,
              orders,
              menu: newMenu,
            });
          },
          () => {
            this.alertService.openSnackBar(
              "Error al obtener submódulos.",
              "error"
            );
          }
        )
        // trace("GetSubmodules")
      );
  }

  @Action(GetPermission)
  getPermissions(
    { patchState, getState }: StateContext<User>,
    { submodule }: GetPermission
  ) {
    const { selectedPlace } = getState();
    if (selectedPlace) {
      return this.api
        .permissionsBySubmoduleAndRole(submodule, selectedPlace.Role.id)
        .pipe(
          tap(
            ({ data: { permissions } }) => {
              patchState({ permissions });
            },
            () => {
              this.alertService.openSnackBar(
                "Error al obtener los permisos.",
                "error"
              );
            }
          )
          //  trace("GetPermission")
        );
    } else {
      return of(null);
    }
  }

  @Action(CleanSelectedPlace)
  cleanSelectedPlace(
    { patchState, dispatch }: StateContext<User>,
    {}: CleanSelectedPlace
  ) {
    patchState({ permissions: null, selectedPlace: null, submodules: null });
    dispatch(new Navigate(["/admin"]));
  }

  @Action(SusbcribeToFCM)
  async susbcribeToFCM({ dispatch }: StateContext<User>) {
    
    const token = await this.fcm.requestPermission();
    return dispatch(new SuscribeToNotification(token));
  }

  @Action(SuscribeToNotification)
  suscribeToNotification(
    { patchState, getState }: StateContext<User>,
    { fcmToken }: SuscribeToNotification
  ) {
    const token = this.store.selectSnapshot(UserState.token);
    const { me } = getState();

    const notification = me.NotificationDevices.find(
      (device) => device.Token_NotificationDevice === fcmToken
    );
    const info = notification
      ? {
          id: notification.id,
          State_NotificationDevice: notification.State_NotificationDevice
            ? 0
            : 1,
          Token_NotificationDevice: fcmToken,
        }
      : {
          State_NotificationDevice: 1,
          Token_NotificationDevice: fcmToken,
        };
    return this.api.suscribeToNotification(info).pipe(
      tap(
        ({ data: { notificationDevice } }) => {
          if (notificationDevice) {
            const clonedMe = _.cloneDeep(me);
            if (info.id) {
              clonedMe.NotificationDevices = clonedMe.NotificationDevices.map(
                (device) =>
                  device.id === notificationDevice.id
                    ? notificationDevice
                    : device
              );
            } else {
              clonedMe.NotificationDevices.push(notificationDevice);
            }
            patchState({
              me: clonedMe,
              devideAdded: notificationDevice
                ? info.State_NotificationDevice === 1
                : true,
            });
          }
        },
        (error) => {
          this.alertService.openSnackBar(
            "Error al crear la notificación.",
            "error"
          );
        }
      )
      //trace("SuscribeToNotification")
    );
  }

  @Action(AdminSubscribeToPlace)
  async AdminSubscribeToPlace(
    { dispatch }: StateContext<User>,
    { id }: AdminSubscribeToPlace
  ) {
    const token = await this.fcm.requestPermission();
    return dispatch(new SubscribeToPlace(id, token));
  }

  @Action(SubscribeToPlace)
  subscribeToPlace(
    { patchState, getState }: StateContext<User>,
    { id, fcmToken }: SubscribeToPlace
  ) {
    const { me } = getState();
    const notification = me.NotificationDevices.find(
      (device) => device.Token_NotificationDevice === fcmToken
    );

    const info = {
      id: notification?.id ?? 0,
      State_NotificationDevice: notification
        ? !notification.State_NotificationDevice
        : false,
      Token_NotificationDevice: fcmToken,
    };

    return this.api
      .subscribeToPlace(
        {
          ...info,
          State_NotificationDevice: info.State_NotificationDevice ? 1 : 0,
        },
        id
      )
      .subscribe((res) => {
        if (res.data.notificationDevice === true) {
          patchState({
            me: {
              ...me,
              NotificationDevices: [
                ...me.NotificationDevices.filter(
                  (d) =>
                    d.Token_NotificationDevice !== info.Token_NotificationDevice
                ),
                info,
              ],
            },
          });
          const message = info.State_NotificationDevice
            ? "activadas"
            : "desactivadas";
          this.alertService.openSnackBar(
            `Notificaciones ${message} correctamente.`,
            "success"
          );
        } else {
          this.alertService.openSnackBar(
            "Error al suscribirse a la tienda.",
            "error"
          );
          this._dataService.displayLoading(false);
        }
      });
  }

  @Action(UpdateNotAvailableProducts)
  UpdateNotAvailableProducts(
    { patchState }: StateContext<User>,
    { notAvailableBillDetails }: UpdateNotAvailableProducts
  ) {
    patchState({ notAvailableBillDetails });
  }

  @Action(UpdateDefaultCoin)
  UpdateDefaultCoin(
    { patchState, getState }: StateContext<User>,
    { FK_Coin }: UpdateDefaultCoin
  ) {
    return this.api.updateDefaultCoin(FK_Coin).pipe(
      tap(({ data: { coin } }) => {
        if (coin) {
          const { me } = getState();
          patchState({ me: { ...me, DefaultCoin: coin } });
        }
      })
      //trace("UpdateDefaultCoin")
    );
  }

  @Action(GetCoins)
  getCoins({ patchState }: StateContext<User>) {
    return this.api.allCoins().pipe(
      tap(
        ({ data: { coins } }) => {
          if (coins) {
            patchState({ coins });
          }
        },
        () => {
          this.alertService.openSnackBar(
            "Error al obtener las monedas.",
            "error"
          );
        }
      )
      // trace("GetCoins")
    );
  }

  @Action(UpdatePlace)
  UpdatePlace(
    { setState, getState, patchState }: StateContext<User>,
    { place }: UpdatePlace
  ) {
    const state = getState();
    const currentPlace = state.selectedPlace; 
    const sortedDelivery = [...place.Delivery].sort((a, b) => a.Radius - b.Radius);
  
    const updatedPlace = {
      ...place,
      Delivery: sortedDelivery,
    };
  
    setState(
      patch({
        places: updateItem<any>(
          (p) => p.id === place.id,
          patch({ ...updatedPlace })
        ),
      })
    );

    patchState({
      selectedPlace: {
        ...currentPlace,
        Delivery: sortedDelivery,
      },
    });
  }

  @Action(Ninja)
  async ninja({ dispatch }: StateContext<User>, { uid, FK_Place }: Ninja) {
    this.afAuth.signOut();
    const {
      data: { ninja },
    } = await this.api.ninja(uid).toPromise();
    this.afAuth.signInWithCustomToken(ninja);
    dispatch(new PlaceProfile(FK_Place));
  }

  @Action(PlaceProfile)
  PlaceProfile(
    { patchState, getState, dispatch }: StateContext<User>,
    { id }: PlaceProfile
  ) {
    return this.api.place(id).pipe(
      tap(({ data: { place } }) => {
        if (place) {
          patchState({ selectedPlace: place });
          dispatch(new GetSubmodules(place));
          dispatch(
            new Navigate(["/admin/products"], null, { replaceUrl: true })
          );
        }
      })
      //trace("PlaceProfile")
    );
  }

  @Action(UploadProfileImage)
  UploadProfileImage(
    { dispatch }: StateContext<User>,
    { file }: UploadProfileImage
  ) {
    const uuidImage = uuid();
    const FirebaseId = `people/${environment.bucket_place}${uuidImage}`;
    const fileRef = this.storage.ref(FirebaseId);
    const uploadTask = this.storage.upload(FirebaseId, file);
    return uploadTask
      .snapshotChanges()
      .pipe(
        finalize(() =>
          dispatch(
            new SaveImageRouteForProfile(fileRef.getDownloadURL(), FirebaseId)
          )
        )
      );
  }

  @Action(SaveImageRouteForProfile)
  SaveImageRouteForProfile(
    { dispatch, setState }: StateContext<User>,
    { fileRef, FirebaseId }: SaveImageRouteForProfile
  ) {
    return fileRef.pipe(
      tap(
        (url) => dispatch(new UpdateProfileImage(url, FirebaseId)),
        () => {
          console.log("error");
        }
      )
    );
  }

  @Action(UpdateProfileImage)
  UpdateProfileImage(
    { patchState, getState }: StateContext<User>,
    { Route_File, FirebaseId }: UpdateProfileImage
  ) {
    return this.api.updateUserProfilePicture(Route_File, FirebaseId).pipe(
      tap(
        ({ data: { updated } }) => {
          try {
            if (updated) {
              const state = getState();
              const newFirebaseUser = {
                ...state.firebaseUser,
                TavuelUser: {
                  ...state.firebaseUser.TavuelUser,
                  Person: {
                    ...state.firebaseUser.TavuelUser.Person,
                    ProfileImage: updated,
                  },
                },
              };

              patchState({
                firebaseUser: newFirebaseUser,
              });
            }
          } catch (error) {
            console.error("Error updating profile image:", error);
          }
        },
        (error) => {
          console.error("API error:", error);
        }
      )
    );
  }

  @Action(GetPermissionsSubmodules)
  GetPermissionsSubmodule(
    { patchState, getState }: StateContext<User>,
    { roleId }: GetPermissionsSubmodules
  ) {
    this.api
      .getPermissionsSubmodules(roleId)
      .subscribe(({ data: { submodules } }) => {
        patchState({ access: submodules });
      });
  }

  @Action(UpdateSelectedPlace)
  updateSelectedPlace(ctx: StateContext<User>, action: UpdateSelectedPlace) {
    ctx.patchState({ selectedPlace: action.payload });
  }

  @Action(UpdatePlaceLogo)
  updatePlaceLogo(ctx: StateContext<User>, action: UpdatePlaceLogo) {
    const state = ctx.getState();
    ctx.patchState({
      selectedPlace: {
        ...state.selectedPlace,
        Logo: action.logo.Route_File,
      },
    });
  }

  @Action(DeleteDeliveryPlace)
  deleteDeliveryPlace(
    { getState, setState, patchState }: StateContext<User>,
    { placeId, deliveryId }: DeleteDeliveryPlace
  ) {
    const state = getState();
    const placeIndex = state.places.findIndex((p) => p.id === placeId);
    if (placeIndex === -1) return;

    const place = state.places[placeIndex];
    const updatedDeliveries = place.Delivery.filter((d) => Number(d.id) !== Number(deliveryId));

    const updatedPlace = {
      ...place,
      Delivery: updatedDeliveries,
    };

    setState(
      patch({
        places: updateItem<any>((p) => Number(p.id) === Number(placeId), patch(updatedPlace)),
      })
    );

    if (state.selectedPlace?.id === placeId) {
      patchState({
        selectedPlace: {
          ...state.selectedPlace,
          Delivery: updatedDeliveries,
        },
      });
    }
  }

  @Action(UpdatePlaceDeliveriesCoordinates)
  updatePlaceDeliveriesCoordinates(
    { getState, patchState, setState }: StateContext<User>,
    { placeId, lat, lng, address }: UpdatePlaceDeliveriesCoordinates
  ) {
    return this.api.setCoordinates(placeId, lat, lng, address).pipe(
      tap((res) => {
        const deliveries = res.data.setCoordinates;

        const state = getState();
        const updatedPlaces = state.places.map((place) =>
          place.id === placeId ? { ...place, Delivery: deliveries } : place
        );

        setState({ ...state, places: updatedPlaces });

        if (state.selectedPlace?.id === placeId) {
          patchState({
            selectedPlace: {
              ...state.selectedPlace,
              Delivery: deliveries,
            },
          });
        }
      })
    );
  }

  getInitMenu() {}
}
